<template>
  <div class="m-map">
    <ul class="m-map__options list-unstyled">
      <li>
        <div class="checkbox">
          <label>
            <input type="checkbox" v-model="showParksMarkers" @change="onLayerSelectionChange"> {{ $t('content.tecnoparks') }}
          </label>
        </div>
      </li>
      <li>
        <div class="checkbox">
          <label>
            <input type="checkbox" v-model="showCompaniesMarkers" @change="onLayerSelectionChange"> {{ $t('commons.companies') }}
          </label>
        </div>
      </li>
    </ul>
    <div ref="mapContainer" class="m-map__canvas" :style="mapCanvasStyle"></div>
    <div v-if="hasActiveLayer" class="m-map__legend">
      <strong>{{ legendTitle }}</strong>
      <span class="m-map__legend-year">{{ currentYear }}</span>
      <div class="m-map__legend-scale">
        <span class="m-map__legend-min">0</span>
        <span class="m-map__legend-gradient" :style="legendGradientStyle"></span>
        <span class="m-map__legend-max">{{ maxStateCount }}</span>
      </div>
      <div v-if="showParksMarkers && showCompaniesMarkers" class="m-map__legend-hint">
        <span class="m-map__legend-dot m-map__legend-dot--parks"></span> {{ $t('content.tecnoparks') }}
        <span class="m-map__legend-dot m-map__legend-dot--companies"></span> {{ $t('commons.companies') }}
      </div>
    </div>
    <p v-else class="m-map__legend m-map__legend--empty">{{ $t('content.mapLegendEmpty') }}</p>
  </div>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { BackendApi } from '@/services/backend_api'
import IndicatorsClient from '@/services/resource_client/indicators'

const BRAZIL_CENTER = [-14.24, -51.93]
const BRAZIL_ZOOM = 4
const BRAZIL_ZOOM_MOBILE = 3
const BRAZIL_CENTER_MOBILE = [-18.5, -52]
const COLOR_EMPTY = '#e8eef2'
const STATE_FILL_OPACITY = 0.75
const BASE_TILE_LAYER = {
  url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
  attribution: '&copy; OpenStreetMap &copy; CARTO',
  subdomains: 'abcd'
}
const CHOROPLETH_PALETTES = {
  parks: { min: '#b8d4e8', max: '#2C698C' },
  companies: { min: '#c5e3f3', max: '#73B0D3' },
  both: { min: '#c8dbe8', max: '#1f5f82' }
}
const MARKER_COLORS = {
  parks: '#2C698C',
  companies: '#73B0D3'
}

export default {
  name: 'Map',
  data () {
    return {
      map: null,
      statesLayer: null,
      parksLayer: null,
      companiesLayer: null,
      parksCountByState: {},
      companiesCountByState: {},
      currentYear: new Date().getFullYear(),
      showParksMarkers: true,
      showCompaniesMarkers: false,
      mapCanvasStyle: 'width: 500px; height: 500px',
      isMobile: false,
      parksMarkersLoaded: false,
      companiesMarkersLoaded: false
    }
  },
  computed: {
    hasActiveLayer () {
      return this.showParksMarkers || this.showCompaniesMarkers
    },
    choroplethMode () {
      if (this.showParksMarkers && this.showCompaniesMarkers) {
        return 'both'
      }
      if (this.showCompaniesMarkers) {
        return 'companies'
      }
      if (this.showParksMarkers) {
        return 'parks'
      }
      return 'none'
    },
    activeCountsByState () {
      if (this.choroplethMode === 'none') {
        return {}
      }

      if (this.choroplethMode === 'parks') {
        return this.parksCountByState
      }

      if (this.choroplethMode === 'companies') {
        return this.companiesCountByState
      }

      const combined = {}
      const states = new Set([
        ...Object.keys(this.parksCountByState),
        ...Object.keys(this.companiesCountByState)
      ])

      states.forEach((uf) => {
        combined[uf] = (this.parksCountByState[uf] || 0) + (this.companiesCountByState[uf] || 0)
      })

      return combined
    },
    maxStateCount () {
      const values = Object.values(this.activeCountsByState)
      return values.length ? Math.max(...values) : 0
    },
    legendTitle () {
      if (this.choroplethMode === 'parks') {
        return this.$t('content.mapLegendParks')
      }
      if (this.choroplethMode === 'companies') {
        return this.$t('content.mapLegendCompanies')
      }
      return this.$t('content.mapLegendBoth')
    },
    legendGradientStyle () {
      const palette = CHOROPLETH_PALETTES[this.choroplethMode] || CHOROPLETH_PALETTES.parks
      return {
        background: `linear-gradient(to right, ${COLOR_EMPTY}, ${palette.min}, ${palette.max})`
      }
    },
    currentPalette () {
      return CHOROPLETH_PALETTES[this.choroplethMode] || CHOROPLETH_PALETTES.parks
    }
  },
  mounted () {
    this.isMobile = document.documentElement.clientWidth <= 550
    if (this.isMobile) {
      this.mapCanvasStyle = 'width: 300px; height: 255px'
    }
    this.initMap()
    this.loadStatesLayer()
    this.loadIndicators()
    this.syncMarkerLayers()
  },
  beforeDestroy () {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    initMap () {
      const center = this.isMobile ? BRAZIL_CENTER_MOBILE : BRAZIL_CENTER
      const zoom = this.isMobile ? BRAZIL_ZOOM_MOBILE : BRAZIL_ZOOM

      this.map = L.map(this.$refs.mapContainer, {
        center: center,
        zoom: zoom,
        scrollWheelZoom: false,
        dragging: false,
        doubleClickZoom: false,
        touchZoom: false,
        boxZoom: false,
        keyboard: false,
        zoomControl: false,
        attributionControl: true
      })

      L.tileLayer(BASE_TILE_LAYER.url, {
        attribution: BASE_TILE_LAYER.attribution,
        subdomains: BASE_TILE_LAYER.subdomains,
        maxZoom: 19
      }).addTo(this.map)

      this.parksLayer = L.layerGroup().addTo(this.map)
      this.companiesLayer = L.layerGroup()
    },
    loadStatesLayer () {
      fetch('/static/geojson/brazil-states.json')
        .then(response => response.json())
        .then(geojson => {
          if (this.statesLayer) {
            this.map.removeLayer(this.statesLayer)
          }

          this.statesLayer = L.geoJSON(geojson, {
            style: (feature) => this.getStateStyle(feature),
            onEachFeature: (feature, layer) => {
              layer.bindTooltip(this.getStateTooltip(feature.properties.UF), { sticky: true })
              layer.on({
                mouseover: (event) => {
                  event.target.setStyle({
                    weight: 2,
                    color: '#1a5276',
                    fillOpacity: Math.min(STATE_FILL_OPACITY + 0.1, 1)
                  })
                },
                mouseout: (event) => {
                  this.statesLayer.resetStyle(event.target)
                }
              })
            }
          }).addTo(this.map)

          this.fitBrazilBounds()
        })
    },
    loadIndicators () {
      IndicatorsClient.get()
        .then((response) => {
          this.parksCountByState = response.data.parksCountByState || {}
          this.companiesCountByState = response.data.companiesCountByState || {}
          this.refreshStatesLayer()
        })
    },
    refreshStatesLayer () {
      if (!this.statesLayer) {
        return
      }

      this.statesLayer.setStyle((feature) => this.getStateStyle(feature))
      this.statesLayer.eachLayer((layer) => {
        const uf = layer.feature.properties.UF
        layer.setTooltipContent(this.getStateTooltip(uf))
      })
    },
    getStateTooltip (uf) {
      const parksCount = this.parksCountByState[uf] || 0
      const companiesCount = this.companiesCountByState[uf] || 0

      if (this.showParksMarkers && this.showCompaniesMarkers) {
        return `${uf}: ${parksCount} ${this.$t('content.mapTooltipParks')}, ${companiesCount} ${this.$t('content.mapTooltipCompanies')}`
      }
      if (this.showCompaniesMarkers) {
        return `${uf}: ${companiesCount} ${this.$t('content.mapTooltipCompanies')}`
      }
      if (this.showParksMarkers) {
        return `${uf}: ${parksCount} ${this.$t('content.mapTooltipParks')}`
      }
      return uf
    },
    getStateStyle (feature) {
      const uf = feature.properties.UF
      const count = this.activeCountsByState[uf] || 0

      return {
        fillColor: this.getStateColor(count),
        weight: 1,
        opacity: 1,
        color: '#ffffff',
        fillOpacity: this.hasActiveLayer ? STATE_FILL_OPACITY : 0.35
      }
    },
    getStateColor (count) {
      if (!this.hasActiveLayer || !count || !this.maxStateCount) {
        return COLOR_EMPTY
      }

      const ratio = count / this.maxStateCount
      const palette = this.currentPalette
      return this.interpolateColor(palette.min, palette.max, ratio)
    },
    interpolateColor (fromHex, toHex, ratio) {
      const from = this.hexToRgb(fromHex)
      const to = this.hexToRgb(toHex)
      const r = Math.round(from.r + (to.r - from.r) * ratio)
      const g = Math.round(from.g + (to.g - from.g) * ratio)
      const b = Math.round(from.b + (to.b - from.b) * ratio)

      return `rgb(${r}, ${g}, ${b})`
    },
    hexToRgb (hex) {
      const normalized = hex.replace('#', '')
      return {
        r: parseInt(normalized.substring(0, 2), 16),
        g: parseInt(normalized.substring(2, 4), 16),
        b: parseInt(normalized.substring(4, 6), 16)
      }
    },
    fitBrazilBounds () {
      if (!this.statesLayer || !this.map) {
        return
      }

      this.map.fitBounds(this.statesLayer.getBounds(), { padding: [8, 8] })
    },
    onLayerSelectionChange () {
      this.refreshStatesLayer()
      this.syncMarkerLayers()
    },
    syncMarkerLayers () {
      if (this.showParksMarkers) {
        if (!this.map.hasLayer(this.parksLayer)) {
          this.parksLayer.addTo(this.map)
        }
        if (!this.parksMarkersLoaded) {
          this.loadParksMarkers()
        }
      } else if (this.map.hasLayer(this.parksLayer)) {
        this.map.removeLayer(this.parksLayer)
      }

      if (this.showCompaniesMarkers) {
        if (!this.map.hasLayer(this.companiesLayer)) {
          this.companiesLayer.addTo(this.map)
        }
        if (!this.companiesMarkersLoaded) {
          this.loadCompaniesMarkers()
        }
      } else if (this.map.hasLayer(this.companiesLayer)) {
        this.map.removeLayer(this.companiesLayer)
      }
    },
    loadParksMarkers () {
      if (this.parksMarkersLoaded) {
        return
      }

      BackendApi.get(BackendApi.getAnonymousClient(), '/lat-lngs/parks')
        .then((response) => {
          this.parksLayer.clearLayers()
          Object.keys(response.data).forEach((key) => {
            if (key === '_links') {
              return
            }
            const point = response.data[key]
            if (!point.lat || !point.lng) {
              return
            }
            L.circleMarker([point.lat, point.lng], {
              radius: 5,
              color: MARKER_COLORS.parks,
              fillColor: MARKER_COLORS.parks,
              fillOpacity: 1,
              weight: 1
            }).addTo(this.parksLayer)
          })
          this.parksMarkersLoaded = true
        })
    },
    loadCompaniesMarkers () {
      if (this.companiesMarkersLoaded) {
        return
      }

      BackendApi.get(BackendApi.getAnonymousClient(), '/lat-lngs/companies')
        .then((response) => {
          this.companiesLayer.clearLayers()
          Object.keys(response.data).forEach((key) => {
            if (key === '_links') {
              return
            }
            const point = response.data[key]
            if (!point.lat || !point.lng) {
              return
            }
            L.circleMarker([point.lat, point.lng], {
              radius: 4,
              color: MARKER_COLORS.companies,
              fillColor: MARKER_COLORS.companies,
              fillOpacity: 1,
              weight: 1
            }).addTo(this.companiesLayer)
          })
          this.companiesMarkersLoaded = true
        })
    }
  }
}
</script>

<style scoped>
.m-map {
  position: relative;
}

.m-map__canvas {
  margin: 0 auto;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
}

.m-map__options {
  margin-bottom: 8px;
}

.m-map__legend {
  margin-top: 10px;
  font-size: 12px;
  color: #444;
  text-align: center;
}

.m-map__legend--empty {
  font-style: italic;
  color: #666;
}

.m-map__legend-year {
  margin-left: 6px;
  color: #666;
}

.m-map__legend-scale {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
}

.m-map__legend-gradient {
  display: inline-block;
  width: 120px;
  height: 10px;
  border-radius: 2px;
}

.m-map__legend-min,
.m-map__legend-max {
  min-width: 16px;
  font-weight: bold;
}

.m-map__legend-hint {
  margin-top: 8px;
  font-size: 11px;
}

.m-map__legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin: 0 4px 0 10px;
}

.m-map__legend-dot--parks {
  background: #2C698C;
}

.m-map__legend-dot--companies {
  background: #73B0D3;
}
</style>

<style>
.m-map__canvas .leaflet-container {
  width: 100%;
  height: 100%;
  background: #f8fbfd;
  font-family: inherit;
}

.m-map__canvas .leaflet-control-attribution {
  font-size: 9px;
}
</style>



// WEBPACK FOOTER //
// Map.vue?4ef5ecd4